import type { ImageSegmenter as TipoSegmentador, ImageSegmenterResult } from '@mediapipe/tasks-vision'
import {
  aparenciaEstaNeutra,
  aparenciaGuardada,
  desfoqueDaPele,
  filtroDeLuz,
  guardarAparencia,
  matrizDeNitidez,
  opacidadeDaSuavizacao,
  type Aparencia,
} from './aparencia.ts'

/**
 * Fundo da câmera na chamada (SPEC-20260916-2346): desfocar o que está atrás
 * de você, ou trocar por uma imagem sua.
 *
 * Como funciona: um modelo recorta a pessoa do resto do quadro; o que sobra é
 * redesenhado (borrado ou com a imagem) num canvas, e é o canvas que vira a
 * imagem enviada. O motor é servido pelo próprio Orbind (`/mediapipe/`, copiado
 * do pacote no build) — sem depender de CDN de terceiro, que empresa com proxy
 * costuma bloquear.
 *
 * Se qualquer coisa falhar (motor não carrega, GPU velha), a câmera segue
 * DIRETA, sem fundo: a chamada nunca fica presa por causa de um enfeite.
 *
 * A APARÊNCIA (SPEC-20260918-0040) entra pelo mesmo caminho, porque é feita da
 * mesma matéria: filtros de canvas, e o canvas já estava aqui. Daí haver TRÊS
 * caminhos, e não dois:
 *
 *   fundo desligado + aparência neutra → `null`: câmera direta, nada processa
 *   fundo desligado + aparência ligada → canvas só com os filtros, SEM modelo
 *                                        nenhum — os 27 MB do MediaPipe não são
 *                                        baixados para clarear uma imagem
 *   fundo ligado                       → o recorte completo, com os filtros da
 *                                        aparência por cima
 */

export type EstiloDeFundo = 'nenhum' | 'desfoque' | 'imagem'

const CHAVE_ESTILO = 'orbind.chamada.fundo'
const CHAVE_IMAGEM = 'orbind.chamada.fundo.imagem'

/**
 * Onde ficam o motor e o modelo, servidos pelo PRÓPRIO Orbind.
 *
 * `scripts/copiar-mediapipe.mjs` põe os dois em `apps/web/public/mediapipe/` no
 * prebuild; a pasta é ignorada pelo git. Nunca apontar para CDN de terceiro.
 *
 * O caminho do motor é só a pasta: o `FilesetResolver` monta dali
 * `vision_wasm_internal.js` e `.wasm` (ou a variante `_nosimd_` quando o
 * navegador não tem SIMD — que não copiamos, então lá o fundo fica
 * indisponível e a câmera segue direta, o comportamento desejado).
 */
const PASTA_DO_MOTOR = '/mediapipe'

/**
 * O modelo de recorte: `selfie_multiclass_256x256`.
 *
 * Trocado em 2026-09-18 pelo antigo `selfie_segmentation` (o leve, de uma única
 * classe "pessoa/não pessoa"). O motivo foi concreto: o encosto da cadeira alta
 * atrás da cabeça do usuário entrava junto com ele, e a borda saía pior que a do
 * Meet. O modelo leve só sabe dizer "isto se parece com o primeiro plano", e
 * cadeira encostada na pessoa se parece bastante.
 *
 * Este aqui responde outra pergunta: para cada pixel, QUAL das seis classes —
 * fundo, cabelo, pele do corpo, pele do rosto, roupa, acessórios. Cadeira não
 * está entre elas, então cai em "fundo" por eliminação, que é exatamente o que
 * resolve o caso relatado.
 */
const MODELO = '/mediapipe/selfie_multiclass_256x256.tflite'
/** Quadros por segundo do processamento: 24 dá conversa fluida sem fritar a CPU. */
const QPS = 24
const DESFOQUE_PX = 18

/* ----------------------- tratamento da máscara (SPEC-20260917-2251) --- */

/**
 * O modelo entrega uma máscara com CONFIANÇA contínua, não um recorte pronto.
 * Usá-la crua traz dois defeitos que o usuário viu comparando com o Meet: a
 * borda vibra a cada quadro, e o que o modelo acha "meio provável" — o encosto
 * da cadeira atrás dos ombros — entra junto com a pessoa. Os três números
 * abaixo tratam disso.
 */

/**
 * Onde a confiança do modelo vira "não é pessoa" e onde vira "é pessoa".
 *
 * Abaixo de ABAIXO_DISSO o pixel some por completo; acima de ACIMA_DISSO ele
 * fica TOTALMENTE opaco; no meio há uma rampa curta, que é a borda macia.
 *
 * A primeira tentativa (2026-09-17 22:51) multiplicava a máscara por si mesma.
 * Aquilo derrubava a cadeira, mas encolhia a escala inteira junto: onde o
 * modelo dizia 0,9 virava 0,73, e a pessoa aparecia TRANSLÚCIDA, com o fundo
 * atravessando ela — "parecendo um fantasma", nas palavras do usuário. O erro
 * foi usar uma curva que só sabe reduzir. Esta aqui separa os dois lados: puxa
 * o baixo para zero e o alto para um.
 */
export const ABAIXO_DISSO_NAO_E_PESSOA = 0.62
export const ACIMA_DISSO_E_PESSOA = 0.8

/**
 * Peso do quadro NOVO na média com os anteriores.
 *
 * Mais baixo acalma mais a borda, porém atrasa a reação a movimento. 0,5 segue
 * conversa normal sem deixar rastro.
 */
export const PESO_DO_QUADRO_NOVO = 0.5

/**
 * Suavização da borda, em pixels da máscara.
 *
 * Era 3, e ficava "embaçado, sem linha nítida" (relato do usuário, 23:31): a
 * máscara já ganha suavidade ao ser ampliada, então 3 pixels somavam borrão
 * sobre borrão. Com 1, o contorno fica definido e ainda sem degrau.
 */
const SUAVIZAR_BORDA_PX = 1

/**
 * Largura em que a máscara é tratada, em pixels — ABSOLUTA, não uma fração da
 * câmera.
 *
 * Era fração (0,5 e depois 0,75). Quando a captura subiu para 720p, "0,75 da
 * resolução" virou 960x540: 518 mil pixels por quadro e 47 MB/s de leitura,
 * contra 77 mil e 7 MB/s de antes. O processamento engasgava e o fundo
 * simplesmente não saía. Em largura fixa o custo não depende mais da câmera —
 * e não se perde nada, porque o modelo enxerga em 256x256: tratar acima disso
 * não acrescenta informação, só conta.
 *
 * Com o ImageSegmenter isto vale em dobro, porque ele devolve a máscara no
 * tamanho do quadro que RECEBEU. Por isso o quadro vai ao modelo já reduzido a
 * esta largura (`telaEntrada`): é o que impede 720p de virar máscara de 921 mil
 * pixels por quadro — a mesma armadilha de antes, por outra porta.
 */
const LARGURA_DA_MASCARA = 512

/**
 * A curva aplicada à confiança de cada pixel, de 0 a 1.
 *
 * Fora e dentro são decididos de vez; só a faixa estreita entre os dois limites
 * vira meio-tom, e é dela que sai o contorno macio.
 */
export function opacidadeDoPixel(confianca: number): number {
  if (confianca <= ABAIXO_DISSO_NAO_E_PESSOA) return 0
  if (confianca >= ACIMA_DISSO_E_PESSOA) return 1
  return (confianca - ABAIXO_DISSO_NAO_E_PESSOA) / (ACIMA_DISSO_E_PESSOA - ABAIXO_DISSO_NAO_E_PESSOA)
}

function novaTela(
  largura: number,
  altura: number,
  lerSempre = false,
): [HTMLCanvasElement, CanvasRenderingContext2D] | null {
  const tela = document.createElement('canvas')
  tela.width = Math.max(1, Math.round(largura))
  tela.height = Math.max(1, Math.round(altura))
  // `willReadFrequently` importa: sem isso o navegador mantém a tela na GPU e
  // cada leitura de pixel vira uma ida e volta cara.
  const ctx = tela.getContext('2d', lerSempre ? { willReadFrequently: true } : undefined)
  return ctx ? [tela, ctx] : null
}

/** Tabela pronta com a curva para os 256 valores possíveis — evita recalcular por pixel. */
const CURVA = new Uint8Array(256)
for (let i = 0; i < 256; i++) CURVA[i] = Math.round(opacidadeDoPixel(i / 255) * 255)

export function estiloDeFundoGuardado(): EstiloDeFundo {
  try {
    const v = localStorage.getItem(CHAVE_ESTILO)
    return v === 'desfoque' || v === 'imagem' ? v : 'nenhum'
  } catch {
    return 'nenhum'
  }
}

export function guardarEstiloDeFundo(estilo: EstiloDeFundo): void {
  try {
    if (estilo === 'nenhum') localStorage.removeItem(CHAVE_ESTILO)
    else localStorage.setItem(CHAVE_ESTILO, estilo)
  } catch {
    /* sem storage: vale só nesta chamada */
  }
}

/** A imagem escolhida, já reduzida, como data URL. */
export function imagemDeFundoGuardada(): string | null {
  try {
    return localStorage.getItem(CHAVE_IMAGEM)
  } catch {
    return null
  }
}

/* ------------------------- a herança do realce fixo (SPEC-20260918-0040) --- */

/**
 * Aqui existia um REALCE fixo — `brightness(1.15) contrast(1.06)` — aplicado a
 * quem ligasse o fundo. O pedido que o criou (2026-09-17 23:43) continua de pé:
 * webcam em sala com luz de teto entrega imagem mais escura que a do Meet. O
 * que saiu foi a decisão estar no meio do código: agora é o controle "clarear"
 * da aparência, que vem de `filtroDeLuz()` — quem tem contraluz de janela
 * precisa de mais, quem tem luz boa não precisa de nada.
 *
 * Só que, para quem JÁ usava fundo, aquele 1,15 não pode simplesmente
 * desaparecer: a imagem escureceria de um dia para o outro sem ninguém ter
 * mexido em nada. Este é o grau de "clarear" que reproduz o realce antigo
 * (0,43 × 0,35 + 1 = 1,150 de brilho), e `aparenciaInicial()` o entrega UMA vez
 * a quem tinha fundo escolhido.
 */
const CLAREAR_QUE_REPETE_O_REALCE_ANTIGO = 0.43

/** Marca de que a herança já foi feita — senão ela voltaria a cada recarga. */
const CHAVE_HERANCA = 'orbind.chamada.aparencia.herdou-realce'

/**
 * A aparência com que a chamada começa.
 *
 * Quase sempre é só o que está guardado. A exceção é a herança descrita acima,
 * que acontece no máximo uma vez por computador: o valor herdado é GUARDADO e
 * aparece no painel, onde dá para aumentar, diminuir ou zerar — e uma vez
 * zerado não volta, porque a marca fica.
 *
 * Quem nunca escolheu fundo não herda nada: sem fundo não havia processamento
 * nenhum antes desta mudança, e não é agora que vai passar a haver.
 */
export function aparenciaInicial(): Aparencia {
  const guardada = aparenciaGuardada()
  if (!aparenciaEstaNeutra(guardada)) return guardada
  try {
    if (localStorage.getItem(CHAVE_HERANCA)) return guardada
    localStorage.setItem(CHAVE_HERANCA, '1')
    if (estiloDeFundoGuardado() === 'nenhum') return guardada
    const herdada: Aparencia = { ...guardada, clarear: CLAREAR_QUE_REPETE_O_REALCE_ANTIGO }
    guardarAparencia(herdada)
    return herdada
  } catch {
    return guardada
  }
}

/**
 * Guarda a imagem escolhida, reduzida para caber no armazenamento do navegador
 * (o original de uma foto de celular tem vários MB e não caberia).
 */
export async function guardarImagemDeFundo(arquivo: File): Promise<string> {
  const bitmap = await createImageBitmap(arquivo)
  const largura = Math.min(1280, bitmap.width)
  const altura = Math.round((bitmap.height / bitmap.width) * largura)
  const tela = document.createElement('canvas')
  tela.width = largura
  tela.height = altura
  tela.getContext('2d')?.drawImage(bitmap, 0, 0, largura, altura)
  bitmap.close()
  const dataUrl = tela.toDataURL('image/jpeg', 0.82)
  try {
    localStorage.setItem(CHAVE_IMAGEM, dataUrl)
  } catch {
    throw new Error('A imagem é grande demais para guardar. Escolha uma menor.')
  }
  return dataUrl
}

export type FundoAplicado = {
  /** A faixa de vídeo já com o fundo — é ela que vai para a chamada. */
  faixa: MediaStreamTrack
  /**
   * Troca estilo, imagem e aparência sem recriar nada (sem piscar a imagem) —
   * é isto que faz arrastar um controle mudar a imagem na hora.
   *
   * Devolve `false` num caso só: este processamento nasceu SEM o modelo de
   * recorte (era aparência com o fundo desligado) e agora pedem fundo. Aí não há
   * o que ajustar — quem chama precisa parar e chamar `aplicarFundoNaCamera` de
   * novo, porque carregar o modelo é `await` e ajuste ao vivo não espera.
   */
  ajustar: (estilo: EstiloDeFundo, imagem: string | null, aparencia: Aparencia) => boolean
  parar: () => void
}

/**
 * Liga o fundo e/ou a aparência numa faixa de câmera. Devolve `null` quando não
 * há nada a fazer, ou quando não dá para processar — aí quem chama segue com a
 * câmera direta.
 */
export async function aplicarFundoNaCamera(
  camera: MediaStreamTrack,
  estilo: EstiloDeFundo,
  imagem: string | null,
  aparencia: Aparencia,
): Promise<FundoAplicado | null> {
  // Tudo desligado: nada processa. Nem canvas, nem faixa nova, nem modelo.
  if (estilo === 'nenhum' && aparenciaEstaNeutra(aparencia)) return null
  /**
   * O modelo de recorte só entra quando há fundo DE VERDADE. Esta variável é a
   * guarda do `await import()` lá embaixo: com ela desligada, os 27 MB do
   * MediaPipe não são baixados nem inicializados — a aparência é filtro de
   * canvas, não precisa saber onde a pessoa está.
   */
  const usaModelo = estilo !== 'nenhum'
  /**
   * Fica fora do `try` de propósito: se algo estourar DEPOIS de o motor abrir
   * (captureStream negado, por exemplo), o `catch` precisa fechá-lo. São ~12 MB
   * de WebAssembly mais uma sessão de WebGL; vazar isso a cada tentativa
   * derruba a aba.
   */
  let segmentador: TipoSegmentador | null = null
  /**
   * E pelo mesmo motivo: se o erro vier depois de o laço de quadros começar,
   * ele precisa parar. Um laço vivo numa tentativa que falhou gastaria CPU
   * desenhando uma tela que ninguém olha, para sempre.
   */
  let desmontar: (() => void) | null = null
  try {
    const ajustes = camera.getSettings()
    const largura = ajustes.width ?? 640
    const altura = ajustes.height ?? 480

    const video = document.createElement('video')
    video.srcObject = new MediaStream([camera])
    video.muted = true
    video.playsInline = true
    await video.play()

    const tela = document.createElement('canvas')
    tela.width = largura
    tela.height = altura
    const ctx = tela.getContext('2d')
    if (!ctx) return null

    let estiloAtual: EstiloDeFundo = estilo
    let aparenciaAtual: Aparencia = aparencia
    let imagemAtual: HTMLImageElement | null = null
    /**
     * A fonte já carregada. Sem isto, cada mexida num controle de aparência
     * chamaria `ajustar` e recarregaria a mesma imagem de fundo do zero —
     * arrastar um controle viraria dezenas de decodificações de data URL.
     */
    let fonteDaImagem: string | null = null
    const carregarImagem = (fonte: string | null) => {
      if (fonte === fonteDaImagem) return
      fonteDaImagem = fonte
      if (!fonte) {
        imagemAtual = null
        return
      }
      const img = new Image()
      img.onload = () => {
        imagemAtual = img
      }
      img.src = fonte
    }
    carregarImagem(imagem)

    /* ------------------- aparência no quadro (SPEC-20260918-0040) --- */

    /**
     * O filtro SVG da nitidez, criado só quando alguém liga a nitidez. Se o
     * ambiente não aceitar `ctx.filter = 'url(#id)'`, a nitidez fica de fora em
     * SILÊNCIO e o resto continua: a invariante é que a chamada nunca trave por
     * causa de um enfeite.
     *
     * ONDE ela pode ser usada não é indiferente. Medido aqui em 2026-09-18, em
     * 1280x720:
     *
     *   convolução no desenho que vai para a TELA DE SAÍDA →   +4 ms por quadro
     *   convolução no desenho que vai para a tela da PESSOA → +190 ms por quadro
     *
     * A tela da pessoa é lida logo depois (ela é a fonte do desenho final), e
     * escrever nela com um filtro SVG para ler em seguida joga a convolução para
     * a CPU — 921 mil pixels de convolução na thread da chamada. Então a nitidez
     * entra SEMPRE no último desenho, o que chega à tela de saída; e isso não
     * muda o resultado, porque a matriz soma 1 e, por isso, comuta com o recorte
     * e com as outras camadas.
     */
    let nitidezSvg: FiltroDeNitidez | null = null
    let nitidezIndisponivel = false
    const referenciaDaNitidez = (): string => {
      const matriz = matrizDeNitidez(aparenciaAtual)
      if (matriz === '' || nitidezIndisponivel) return ''
      if (!nitidezSvg) {
        nitidezSvg = criarFiltroDeNitidez()
        if (!nitidezSvg) {
          nitidezIndisponivel = true
          return ''
        }
      }
      nitidezSvg.definirMatriz(matriz)
      return nitidezSvg.referencia
    }

    /**
     * Desenha o quadro da câmera com a aparência aplicada, na ordem que importa:
     * SUAVIZAR → CLAREAR → NITIDEZ. A nitidez vem depois da suavização porque
     * uma desfaz a outra: realçar borda antes de borrar é trabalho jogado fora.
     *
     * São DUAS camadas no máximo: a nítida e, por cima, a mesma desfocada com
     * transparência parcial — que é o foco suave. `recortar` liga o recorte da
     * pessoa: a primeira camada entra com `source-in` (é a máscara que está no
     * destino) e a segunda com `source-atop`, que mistura por cima SEM mexer no
     * alpha do recorte.
     *
     * A ORDEM PEDIDA SAI DE GRAÇA, sem uma passada a mais na imagem, porque as
     * duas camadas somam 1 de peso e os três ajustes são contas afins (brilho e
     * contraste; e a matriz de nitidez soma 1, de propósito): aplicá-los a cada
     * camada dá exatamente o mesmo resultado que aplicá-los à soma das duas.
     *
     * Foi medido. A primeira versão compunha as camadas numa tela de trabalho
     * para filtrar o resultado: 31 ms por quadro em 1280x720, contra 0,6 ms
     * assim — porque tela escrita com filtro e lida no MESMO quadro joga o
     * filtro para a CPU.
     */
    const desenharQuadro = (
      destino: CanvasRenderingContext2D,
      l: number,
      a: number,
      recortar: boolean,
      nitidez: string,
    ) => {
      const luz = filtroDeLuz(aparenciaAtual)
      const desfoque = desfoqueDaPele(aparenciaAtual)
      destino.globalCompositeOperation = recortar ? 'source-in' : 'source-over'
      destino.filter = juntarFiltros(luz, nitidez)
      destino.drawImage(video, 0, 0, l, a)
      destino.filter = 'none'
      if (desfoque <= 0) return
      destino.globalCompositeOperation = recortar ? 'source-atop' : 'source-over'
      destino.globalAlpha = opacidadeDaSuavizacao(aparenciaAtual)
      destino.filter = juntarFiltros(`blur(${desfoque.toFixed(2)}px)`, luz, nitidez)
      destino.drawImage(video, 0, 0, l, a)
      destino.globalAlpha = 1
      destino.filter = 'none'
    }

    /** A pessoa recortada, quando há modelo; no caminho barato fica `null`. */
    let telaPessoa: HTMLCanvasElement | null = null

    const desenharSaida = (temMascara: boolean) => {
      ctx.save()
      ctx.clearRect(0, 0, tela.width, tela.height)
      if (estiloAtual === 'nenhum') {
        // Só aparência: não há fundo a trocar, então o quadro INTEIRO passa
        // pelos filtros — e nenhum modelo precisou rodar para isso.
        desenharQuadro(ctx, tela.width, tela.height, false, referenciaDaNitidez())
      } else if (estiloAtual === 'imagem' && imagemAtual) {
        // A imagem escolhida entra como ela é: tem a exposição dela, e clarear
        // uma foto de praia porque o rosto de quem está na frente pedia luz
        // seria mexer no que não foi pedido.
        desenharCobrindo(ctx, imagemAtual, tela.width, tela.height)
      } else {
        // o fundo leva o MESMO clarear da pessoa, senão um fica mais claro que o
        // outro; suavizar e nitidez não têm o que fazer no que já está borrado
        ctx.filter = juntarFiltros(`blur(${DESFOQUE_PX}px)`, filtroDeLuz(aparenciaAtual))
        ctx.drawImage(video, 0, 0, tela.width, tela.height)
        ctx.filter = 'none'
      }
      // SAÍDA: o fundo inteiro primeiro, a pessoa por cima. Fazer o contrário
      // (fundo entrando por baixo do recorte) deixava um halo do contorno.
      //
      // É NESTE desenho que a nitidez da pessoa entra — o último, o que chega à
      // tela de saída. Ver `referenciaDaNitidez()` para o porquê.
      if (temMascara && telaPessoa && estiloAtual !== 'nenhum') {
        ctx.filter = juntarFiltros(referenciaDaNitidez())
        ctx.drawImage(telaPessoa, 0, 0)
        ctx.filter = 'none'
      }
      ctx.restore()
    }

    let vivo = true
    let ultimoEnvio = 0
    /**
     * A marca de tempo que o modo VIDEO usa para ordenar os quadros: precisa
     * ser ESTRITAMENTE crescente, senão o motor descarta o quadro. Dois
     * `performance.now()` seguidos podem arredondar para o mesmo milissegundo.
     */
    let ultimaMarca = -1
    const intervalo = 1000 / QPS

    /**
     * Um quadro pelo modelo. Fica `null` no caminho barato (só aparência) — e é
     * por essa porta que nada de MediaPipe é carregado.
     */
    let segmentarQuadro: ((agora: number) => void) | null = null

    if (usaModelo) {
      // Telas de trabalho da máscara: acompanham a PROPORÇÃO do vídeo, com
      // largura fixa (ver LARGURA_DA_MASCARA).
      const lm = Math.min(LARGURA_DA_MASCARA, largura)
      const am = Math.round((altura / largura) * lm)
      // O quadro reduzido que vai ao modelo. É ele que fixa o tamanho da máscara,
      // porque o ImageSegmenter devolve a máscara no tamanho do que recebeu.
      const entrada = novaTela(lm, am)
      // escreve pixel a cada quadro (putImageData) — daí o `willReadFrequently`
      const acumulada = novaTela(lm, am, true)
      const pessoa = novaTela(largura, altura)
      if (!entrada || !acumulada || !pessoa) return null
      const [telaEntrada, ctxEntrada] = entrada
      const [telaAcumulada, ctxAcumulada] = acumulada
      const [telaDaPessoa, ctxPessoa] = pessoa
      telaPessoa = telaDaPessoa

      /** A confiança média acumulada (escala 0–255), um valor por pixel da máscara. */
      let acumulado = new Float32Array(telaAcumulada.width * telaAcumulada.height)
      /** A máscara pronta que sai daqui: branca, com o alpha já decidido. */
      let mascaraPronta = ctxAcumulada.createImageData(telaAcumulada.width, telaAcumulada.height)
      const pintarDeBranco = (dados: ImageData) => {
        for (let i = 0; i < dados.data.length; i += 4) {
          dados.data[i] = 255
          dados.data[i + 1] = 255
          dados.data[i + 2] = 255
        }
      }
      pintarDeBranco(mascaraPronta)

      /**
       * Refaz os buffers se o modelo devolver a máscara num tamanho diferente do
       * que pedimos. Não deveria acontecer — mandamos um quadro de tamanho fixo —
       * mas ler `acumulado` fora do fim daria `undefined`, e `undefined` em conta
       * de ponto flutuante vira NaN, que envenena a média para sempre.
       */
      const ajustarBuffers = (l: number, a: number) => {
        if (telaAcumulada.width === l && telaAcumulada.height === a) return
        telaAcumulada.width = l
        telaAcumulada.height = a
        acumulado = new Float32Array(l * a)
        mascaraPronta = ctxAcumulada.createImageData(l, a)
        pintarDeBranco(mascaraPronta)
      }

      // a biblioteca só é baixada quando alguém liga o FUNDO pela primeira vez
      const { FilesetResolver, ImageSegmenter } = await import('@mediapipe/tasks-vision')
      const motor = await FilesetResolver.forVisionTasks(PASTA_DO_MOTOR)
      segmentador = await ImageSegmenter.createFromOptions(motor, {
        baseOptions: {
          modelAssetPath: MODELO,
          /*
           * `GPU` NÃO é otimização opcional aqui — é o que torna a troca viável.
           *
           * Medido em 2026-09-18 nesta máquina, quadro de 512x288, já incluindo a
           * leitura da máscara de volta para a CPU:
           *
           *   delegate padrão (= CPU, XNNPACK) → 413 ms por quadro
           *   delegate 'GPU'                   →  17 ms (p90 21 ms)
           *
           * O orçamento a 24 QPS é 41,7 ms. No CPU o multiclass estoura dez vezes
           * e travaria a aba junto, porque `segmentForVideo` roda na thread
           * principal: seria trocar a cadeira no fundo por uma chamada aos
           * pedaços. Se a GPU não servir, `createFromOptions` estoura, o `catch`
           * devolve null e a câmera segue direta — o que é o certo a fazer.
           *
           * O primeiro quadro custa ~900 ms (compilação dos shaders) e os dois
           * seguintes ~50 ms; depois estabiliza. É uma vez por chamada.
           */
          delegate: 'GPU',
        },
        // VIDEO faz o modelo aproveitar o quadro anterior; em IMAGE cada quadro
        // sairia do zero e a borda voltaria a vibrar.
        runningMode: 'VIDEO',
        // Queremos a CONFIANÇA contínua, não o `categoryMask`. O mask de
        // categoria já é o argmax — um recorte duro de 0 ou 1 — e é justamente a
        // faixa contínua que alimenta a média e a curva daqui de baixo.
        outputConfidenceMasks: true,
        outputCategoryMask: false,
      })
      const segm = segmentador

      const aoSegmentar = (resultado: ImageSegmenterResult) => {
        /*
         * O modelo devolve uma máscara de confiança POR CLASSE, na ordem
         * 0=fundo, 1=cabelo, 2=pele do corpo, 3=pele do rosto, 4=roupa,
         * 5=acessórios. Como as seis somam 1 em cada pixel, "é pessoa" = 1 menos
         * o fundo — uma leitura de array em vez de cinco somas, com o mesmo
         * resultado.
         *
         * Vale dizer por que NÃO descontamos "acessórios" junto: óculos, boné e
         * fone caem nessa classe, e tirá-los abriria buracos no rosto. A cadeira
         * do relato não é acessório de ninguém — ela cai em "fundo", que é o
         * ponto todo da troca de modelo.
         */
        const mascara = resultado.confidenceMasks?.[0]
        const temMascara = !!mascara

        if (mascara) {
          const cru = mascara.getAsFloat32Array()
          ajustarBuffers(mascara.width, mascara.height)
          if (cru.length < acumulado.length) return

          /*
           * MÉDIA e DECISÃO, em aritmética de verdade.
           *
           * Uma versão antiga fazia a média empilhando operações de canvas
           * (`destination-out` para descontar o antigo, `source-over` para somar
           * o novo). Aquilo não era média: o `source-over` já pondera pelo alpha
           * da fonte, então o desconto entrava DUAS vezes e a conta convergia
           * para 0,667 — abaixo do limiar. A pessoa ficava presa em ~77% de
           * opacidade para sempre, o que no fundo desfocado não se percebe (atrás
           * de você aparece você, borrado) mas com imagem de fundo salta à vista.
           *
           * Aqui a média é a expressão que ela deveria ser desde o começo, e a
           * curva é aplicada em cima dela: o que é pessoa vira opaco de verdade.
           */
          for (let p = 0, i = 3; p < acumulado.length; p++, i += 4) {
            const agora = (1 - cru[p]!) * 255
            const media = acumulado[p]! * (1 - PESO_DO_QUADRO_NOVO) + agora * PESO_DO_QUADRO_NOVO
            acumulado[p] = media
            mascaraPronta.data[i] = CURVA[media < 0 ? 0 : media > 255 ? 255 : media | 0]!
          }
          ctxAcumulada.putImageData(mascaraPronta, 0, 0)

          // PESSOA recortada, com a borda suavizada na ampliação. O pixel vem do
          // `video` em resolução cheia, não do quadro reduzido que o modelo viu:
          // a máscara é que é barata, a imagem não precisa ser.
          ctxPessoa.globalCompositeOperation = 'source-over'
          ctxPessoa.clearRect(0, 0, largura, altura)
          ctxPessoa.filter = `blur(${SUAVIZAR_BORDA_PX}px)`
          ctxPessoa.drawImage(telaAcumulada, 0, 0, largura, altura)
          ctxPessoa.filter = 'none'
          // A pessoa entra recortada pela máscara e já com a aparência — menos a
          // nitidez, que vai no desenho da tela de saída, em `desenharSaida`.
          desenharQuadro(ctxPessoa, largura, altura, true, '')
          ctxPessoa.globalCompositeOperation = 'source-over'
        }

        desenharSaida(temMascara)
      }

      segmentarQuadro = (agora: number) => {
        // o modelo recebe o quadro JÁ REDUZIDO — é isto que fixa o custo por
        // quadro, independente da resolução da câmera
        ctxEntrada.drawImage(video, 0, 0, lm, am)
        const marca = Math.max(Math.round(agora), ultimaMarca + 1)
        ultimaMarca = marca
        try {
          /*
           * `segmentForVideo` com callback é SÍNCRONO: volta só depois de o
           * callback terminar. Não há mais o que empilhar, e por isso a trava
           * "uma segmentação por vez" da versão com `send()` saiu — ela agora
           * seria só um booleano que nunca fica ligado.
           */
          segm.segmentForVideo(telaEntrada, marca, aoSegmentar)
        } catch {
          /* um quadro perdido não derruba a chamada */
        }
      }
    }

    const processar = () => {
      if (!vivo || video.readyState < 2) return
      const agora = performance.now()
      if (agora - ultimoEnvio < intervalo) return
      ultimoEnvio = agora
      /*
       * Fundo desligado: o modelo NÃO roda — nem quando já está carregado (a
       * pessoa pode ter desligado o fundo e ficado só com a aparência). São os
       * 17 ms de GPU por quadro que deixam de ser gastos para nada.
       *
       * Ao religar o fundo, a média por pixel recomeça do que ficou guardado e
       * converge em três ou quatro quadros — o mesmo que acontece ao ligar o
       * fundo pela primeira vez.
       */
      if (!segmentarQuadro || estiloAtual === 'nenhum') {
        desenharSaida(false)
        return
      }
      segmentarQuadro(agora)
    }

    /**
     * Acompanha o QUADRO do vídeo quando o navegador oferece isso, em vez de um
     * relógio próprio: processar fora de hora gasta CPU refazendo a mesma
     * imagem, e perde quadro quando a câmera entrega mais rápido que o relógio.
     */
    type VideoComQuadro = HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number
      cancelVideoFrameCallback?: (id: number) => void
    }
    const videoComQuadro = video as VideoComQuadro
    let relogio: ReturnType<typeof setInterval> | null = null
    let pedidoDeQuadro = 0
    /**
     * Um quadro que falha NÃO pode matar o laço.
     *
     * A versão anterior reagendava DEPOIS de processar: bastava uma exceção em
     * um quadro para o `requestVideoFrameCallback` nunca mais ser pedido, e a
     * imagem congelava para sempre — foi o que o usuário viu ao ligar o retoque
     * (2026-09-18 16:13). Agora o próximo quadro é pedido ANTES, e o erro é
     * contado em vez de interromper.
     *
     * Se os erros persistirem, o processamento se desmonta e a câmera volta a
     * ser direta. É a invariante da SPEC: sem efeito é aceitável, tela
     * congelada nunca.
     */
    const LIMITE_DE_ERROS = 12
    let errosSeguidos = 0

    /**
     * Última linha de defesa: encerra o laço e deixa o quadro atual parado.
     *
     * Quem chama percebe pela faixa e pode recriar; o importante é não seguir
     * tentando desenhar 24 vezes por segundo o que já falhou doze vezes.
     */
    const desistirDoProcessamento = () => {
      vivo = false
      if (relogio !== null) clearInterval(relogio)
      videoComQuadro.cancelVideoFrameCallback?.(pedidoDeQuadro)
    }

    const processarComRede = () => {
      try {
        processar()
        errosSeguidos = 0
      } catch {
        errosSeguidos += 1
        if (errosSeguidos >= LIMITE_DE_ERROS) desistirDoProcessamento()
      }
    }

    if (typeof videoComQuadro.requestVideoFrameCallback === 'function') {
      const laco = () => {
        if (!vivo) return
        // pedido ANTES de desenhar: é isto que impede o laço de morrer
        pedidoDeQuadro = videoComQuadro.requestVideoFrameCallback!(laco)
        processarComRede()
      }
      pedidoDeQuadro = videoComQuadro.requestVideoFrameCallback(laco)
    } else {
      relogio = setInterval(processarComRede, Math.round(intervalo))
    }

    const encerrarLaco = () => {
      vivo = false
      if (relogio !== null) clearInterval(relogio)
      videoComQuadro.cancelVideoFrameCallback?.(pedidoDeQuadro)
      // o filtro da nitidez é um nó no documento: sai daqui, senão cada chamada
      // deixaria um SVG para trás na página
      nitidezSvg?.remover()
      nitidezSvg = null
    }
    desmontar = encerrarLaco

    /*
     * Um quadro direto na tela antes de capturar a faixa.
     *
     * O primeiro `segmentForVideo` gasta ~900 ms compilando shaders, e até ele
     * terminar a `tela` nunca foi desenhada — `captureStream` de tela virgem
     * manda PRETO para a chamada. Um segundo de tela preta ao ligar o fundo é
     * pior que um segundo sem o fundo.
     */
    if (video.readyState >= 2) ctx.drawImage(video, 0, 0, tela.width, tela.height)

    const saida = tela.captureStream(QPS).getVideoTracks()[0]
    if (!saida) {
      encerrarLaco()
      if (segmentador) fecharSegmentador(segmentador)
      return null
    }
    // texto de tela compartilhada não passa por aqui; isto é rosto em movimento
    saida.contentHint = 'motion'

    return {
      faixa: saida,
      ajustar: (novoEstilo, novaImagem, novaAparencia) => {
        // pedem fundo a um processamento que nasceu sem o modelo: só recriando
        if (novoEstilo !== 'nenhum' && !segmentarQuadro) return false
        estiloAtual = novoEstilo
        aparenciaAtual = novaAparencia
        carregarImagem(novaImagem)
        return true
      },
      parar: () => {
        encerrarLaco()
        saida.stop()
        video.srcObject = null
        if (segmentador) fecharSegmentador(segmentador)
      },
    }
  } catch {
    // o laço e o motor podem já estar de pé quando o erro veio de outro lugar
    desmontar?.()
    if (segmentador) fecharSegmentador(segmentador)
    return null
  }
}

/** `close()` do ImageSegmenter é síncrono e pode estourar; nunca deixar escapar. */
function fecharSegmentador(segmentador: TipoSegmentador): void {
  try {
    segmentador.close()
  } catch {
    /* já fechado ou contexto perdido: nada a fazer */
  }
}

/** Desenha a imagem cobrindo o quadro inteiro, sem distorcer (como `object-fit: cover`). */
function desenharCobrindo(ctx: CanvasRenderingContext2D, img: HTMLImageElement, largura: number, altura: number) {
  const escala = Math.max(largura / img.width, altura / img.height)
  const l = img.width * escala
  const a = img.height * escala
  ctx.drawImage(img, (largura - l) / 2, (altura - a) / 2, l, a)
}

/** Junta filtros de canvas numa string só; nada a aplicar vira `none`. */
function juntarFiltros(...partes: string[]): string {
  const usadas = partes.filter((p) => p !== '')
  return usadas.length > 0 ? usadas.join(' ') : 'none'
}

/* ------------------------- a nitidez (SPEC-20260918-0040) --- */

type FiltroDeNitidez = {
  /** O que vai em `ctx.filter`: `url(#id)`. */
  referencia: string
  definirMatriz: (matriz: string) => void
  remover: () => void
}

/** Para dois processamentos na mesma página não disputarem o mesmo id. */
let filtrosCriados = 0

/**
 * O filtro SVG da nitidez, posto no documento.
 *
 * O canvas 2D não tem filtro de nitidez — nitidez é convolução, e a única
 * convolução que o `ctx.filter` alcança é um `feConvolveMatrix` de um SVG que
 * esteja NO DOCUMENTO, referenciado por `url(#id)`. Então há um nó de SVG por
 * processamento, com id próprio, e ele sai junto no `parar()`: sem isso cada
 * chamada deixaria um para trás.
 *
 * Devolve `null` quando o ambiente não aceita a referência — e aí a nitidez
 * simplesmente não existe, sem aviso e sem quebrar nada.
 */
function criarFiltroDeNitidez(): FiltroDeNitidez | null {
  try {
    const NS = 'http://www.w3.org/2000/svg'
    const id = `orbind-nitidez-${++filtrosCriados}-${Math.random().toString(36).slice(2, 8)}`
    const svg = document.createElementNS(NS, 'svg')
    svg.setAttribute('width', '0')
    svg.setAttribute('height', '0')
    svg.setAttribute('aria-hidden', 'true')
    svg.style.position = 'absolute'
    svg.style.width = '0'
    svg.style.height = '0'
    svg.style.overflow = 'hidden'
    const filtro = document.createElementNS(NS, 'filter')
    filtro.setAttribute('id', id)
    // sRGB é o espaço em que o canvas já trabalha. No padrão do SVG (linearRGB)
    // a mesma matriz clareia a imagem junto, e o realce sai com outra cara.
    filtro.setAttribute('color-interpolation-filters', 'sRGB')
    const convolucao = document.createElementNS(NS, 'feConvolveMatrix')
    convolucao.setAttribute('order', '3')
    // a matriz mexe só na cor; o alpha passa intacto. O quadro que entra aqui é
    // sempre opaco, mas assim o recorte da pessoa nunca ganha franja na borda.
    convolucao.setAttribute('preserveAlpha', 'true')
    convolucao.setAttribute('kernelMatrix', MATRIZ_NEUTRA)
    filtro.appendChild(convolucao)
    svg.appendChild(filtro)
    document.body.appendChild(svg)
    const referencia = `url(#${id})`
    const remover = () => svg.remove()
    if (!aReferenciaFunciona(referencia)) {
      remover()
      return null
    }
    return {
      referencia,
      definirMatriz: (matriz) => {
        if (convolucao.getAttribute('kernelMatrix') !== matriz) convolucao.setAttribute('kernelMatrix', matriz)
      },
      remover,
    }
  } catch {
    return null
  }
}

/** Convolução que não faz nada: só o pixel do meio, peso 1. */
const MATRIZ_NEUTRA = '0 0 0 0 1 0 0 0 0'

/**
 * `ctx.filter = 'url(#id)'` não existe em todo navegador, e onde existe pode não
 * resolver a referência. Em vez de confiar na string, desenha 4x4 de cinza
 * ATRAVÉS do filtro (que ainda está neutro) e confere se saiu cinza: onde o
 * filtro não vale, sai transparente — e a nitidez fica de fora em silêncio.
 */
function aReferenciaFunciona(referencia: string): boolean {
  try {
    const fonte = novaTela(4, 4)
    const alvo = novaTela(4, 4, true)
    if (!fonte || !alvo) return false
    fonte[1].fillStyle = '#808080'
    fonte[1].fillRect(0, 0, 4, 4)
    alvo[1].filter = referencia
    if (!alvo[1].filter.includes('url')) return false
    alvo[1].drawImage(fonte[0], 0, 0)
    alvo[1].filter = 'none'
    const pixel = alvo[1].getImageData(2, 2, 1, 1).data
    return pixel[3]! > 200 && pixel[0]! > 60
  } catch {
    return false
  }
}
